// The JWT lives in sessionStorage (per-tab, cleared on tab close) rather
// than localStorage — one-tab XSS is still catastrophic, but a compromised
// dep on a sibling tab can't skim the token forever from a persistent
// storage bucket. The cross-cutting fix is HttpOnly cookies + CSRF, which
// requires backend changes; this closes the persistent-token half of the
// finding today.
const TOKEN_KEY = "report-bot.token";
const _mem: { token: string | null } = { token: null };

type UnauthorizedHandler = () => void;
let _onUnauthorized: UnauthorizedHandler = () => {};

export function registerUnauthorizedHandler(fn: UnauthorizedHandler): void {
  _onUnauthorized = fn;
}

export const auth = {
  token: (): string | null => {
    if (_mem.token) return _mem.token;
    try {
      _mem.token = sessionStorage.getItem(TOKEN_KEY);
    } catch {
      _mem.token = null;
    }
    return _mem.token;
  },
  setToken: (t: string): void => {
    _mem.token = t;
    try {
      sessionStorage.setItem(TOKEN_KEY, t);
    } catch {
      // Storage disabled — token still lives in memory for this tab.
    }
  },
  clear: (): void => {
    _mem.token = null;
    try {
      sessionStorage.removeItem(TOKEN_KEY);
    } catch {
      // ignored
    }
  },
};

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
  }
}

function formatDetail(body: unknown, fallback: string): string {
  if (typeof body === "string" && body.trim()) return body;
  if (body && typeof body === "object" && "detail" in body) {
    const detail = (body as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
    // FastAPI 422 shape: [{loc: [...], msg: "...", type: "..."}].
    if (Array.isArray(detail)) {
      return detail
        .map((d) => {
          if (d && typeof d === "object" && "msg" in d) {
            const msg = String((d as { msg: unknown }).msg);
            const loc = (d as { loc?: unknown[] }).loc;
            const where = Array.isArray(loc) ? loc.filter((p) => p !== "body").join(".") : "";
            return where ? `${where}: ${msg}` : msg;
          }
          return String(d);
        })
        .join("; ");
    }
  }
  return fallback;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  const t = auth.token();
  if (t) headers.set("Authorization", `Bearer ${t}`);

  const r = await fetch(path, { ...init, headers });
  if (r.status === 401) {
    // Central redirect — every hook sees a consistent state instead of a
    // patchwork of red error blocks under a protected page.
    auth.clear();
    _onUnauthorized();
    throw new ApiError(401, "Session expired");
  }
  if (r.status === 204) return undefined as T;
  const body = r.headers.get("content-type")?.includes("application/json")
    ? await r.json().catch(() => null)
    : await r.text().catch(() => "");
  if (!r.ok) {
    throw new ApiError(r.status, formatDetail(body, r.statusText || `HTTP ${r.status}`));
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
